import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import { getActivityLabel, getScenarioLabel, SCENARIO_LABEL } from '@/utils/labels';
import AptoMedicoChip from '@/components/AptoMedicoChip';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Filter, 
  User, 
  AlertTriangle,
  Trash2,
  Eye,
  Loader2,
  FileText,
  Heart,
  Activity,
  Users,
  Check,
  ChevronDown,
  Link as LinkIcon,
  ClipboardList
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { StudentIntakeLinkModal } from '@/components/StudentIntakeLinkModal';
import { intensityUnlock } from '@/utils/decisionState';

// Fase B — helper para detectar entrevistas cargadas por el alumno pendientes de revisión.
const isPendienteRevision = (e) =>
  e?.origen === 'alumno' && e?.lifecycle_status === 'pendiente_revision';

// Mapeo del nuevo motor de 4 estados al filtro/badge del listado.
const ESTADO_BADGE = {
  PUEDE_INICIAR: 'bg-green-100 text-green-700 border-green-200',
  APTO_PARA_EJERCICIO_MODERADO: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  APTO_PARA_PROGRESAR_CON_CONTROL: 'bg-amber-100 text-amber-800 border-amber-300',
  APTO_REQUERIDO: 'bg-orange-100 text-orange-800 border-orange-300',
  DERIVACION_INMEDIATA: 'bg-red-100 text-red-700 border-red-300',
  // Aliases legacy (datos viejos en BD aún no migrados)
  APTO_RECOMENDABLE: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  APTO_PARA_PROGRESAR: 'bg-amber-100 text-amber-800 border-amber-300',
};

// Labels canónicos de los 5 estados — DEBEN coincidir con headline en decisionState.js,
// con el PDF (decision_presentation.py) y el Informe Final. Un solo vocabulario.
const ESTADO_SHORT_LABEL = SCENARIO_LABEL;

const _LEGACY_ESTADO_ALIAS = {
  APTO_RECOMENDABLE: 'APTO_PARA_EJERCICIO_MODERADO',
  APTO_PARA_PROGRESAR: 'APTO_PARA_PROGRESAR_CON_CONTROL',
};

const _normalizeEstado = (e) => _LEGACY_ESTADO_ALIAS[e] || e || 'PUEDE_INICIAR';
const getEstado = (e) => _normalizeEstado(e?.exercise_recommendation?.estado);

const getDecisionBadgeClass = (estado) => ESTADO_BADGE[estado] || ESTADO_BADGE.PUEDE_INICIAR;
const getDecisionLabel = (estado) => getScenarioLabel(estado);

// Helper to calculate Karvonen heart rate zones
const calculateKarvonenZones = (maxHR, restingHR) => {
  if (!maxHR || !restingHR) return null;
  const hrReserve = maxHR - restingHR;
  const percentages = [40, 50, 60, 70, 80, 90];
  return percentages.map(p => ({
    percent: p,
    hr: Math.round(restingHR + (hrReserve * p / 100))
  }));
};

// Cardiorespiratory Metrics Component
const CardioMetrics = ({ evaluation }) => {
  const maxHR = evaluation.max_heart_rate;
  const restingHR = evaluation.resting_heart_rate;
  const hrReserve = maxHR && restingHR ? Math.round(maxHR - restingHR) : null;
  // Solo los porcentajes de las intensidades que habilita el escenario (y el
  // apto, si lo trajo): debajo de 60 % es moderada, desde 60 % vigorosa.
  const unlock = intensityUnlock(
    evaluation.exercise_recommendation?.estado,
    evaluation.apto_medico_entregado,
    evaluation.apto_intensidades_habilitadas,
  );
  const karvonenTodas = calculateKarvonenZones(maxHR, restingHR);
  const permitidas = (karvonenTodas || []).filter((z) =>
    (z.percent < 60 ? unlock.moderate.enabled : unlock.vigorous.enabled));
  const karvonenZones = evaluation.betabloqueante === true || permitidas.length === 0 ? null : permitidas;

  if (!maxHR) return null;

  return (
    <div className="mt-3 pt-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Heart className="h-3.5 w-3.5 text-[#1F3A5F]" />
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide font-['Inter']">
          Métricas Cardiorrespiratorias
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-blue-50 rounded-md p-2 text-center">
          <p className="text-[10px] text-blue-600 font-medium font-['Inter']">FC Máx</p>
          <p className="font-mono font-bold text-blue-800 text-sm">{Math.round(maxHR)}</p>
        </div>
        {restingHR && (
          <div className="bg-green-50 rounded-md p-2 text-center">
            <p className="text-[10px] text-green-600 font-medium font-['Inter']">FC Reposo</p>
            <p className="font-mono font-bold text-green-800 text-sm">{Math.round(restingHR)}</p>
          </div>
        )}
        {hrReserve && (
          <div className="bg-purple-50 rounded-md p-2 text-center">
            <p className="text-[10px] text-purple-600 font-medium font-['Inter']">Reserva FC</p>
            <p className="font-mono font-bold text-purple-800 text-sm">{hrReserve}</p>
          </div>
        )}
        {karvonenZones && (
          <div className="bg-amber-50 rounded-md p-2 text-center">
            <p className="text-[10px] text-amber-600 font-medium font-['Inter']">Karvonen</p>
            <p className="font-mono font-bold text-amber-800 text-xs">
              {karvonenZones[0].hr}-{karvonenZones[karvonenZones.length - 1].hr}
            </p>
          </div>
        )}
      </div>
      {karvonenZones && (
        <div className="mt-2 flex flex-wrap gap-1">
          {karvonenZones.map((zone, idx) => (
            <span 
              key={zone.percent}
              className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono font-['Inter']"
            >
              {zone.percent}%: {zone.hr}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function EvaluationsPage() {
  const [evaluations, setEvaluations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterPendingApto, setFilterPendingApto] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // group filter: 'all' | 'none' | <group_id>
  const groupFilter = searchParams.get('group_id') || 'all';

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (groupFilter && groupFilter !== 'all') {
        filters.groupId = groupFilter;
      }
      const [evalData, groupData] = await Promise.all([
        api.getEvaluations(filters),
        api.getGroups().catch(() => []),
      ]);
      setEvaluations(evalData);
      setGroups(groupData || []);
    } catch (error) {
      console.error('Error loading evaluations:', error);
      toast.error('Error al cargar evaluaciones');
    } finally {
      setLoading(false);
    }
  };

  const groupNameById = React.useMemo(() => {
    const m = new Map();
    (groups || []).forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const onChangeGroupFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete('group_id');
    } else {
      next.set('group_id', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleAssignGroup = async (evaluationId, newGroupId) => {
    try {
      await api.assignEvaluationGroup(evaluationId, newGroupId);
      // Optimistic local update
      setEvaluations((prev) => prev.map((e) =>
        e.id === evaluationId ? { ...e, group_id: newGroupId } : e
      ));
      const groupName = newGroupId ? groupNameById.get(newGroupId) : null;
      toast.success(groupName ? `Asignado a "${groupName}"` : 'Removido del grupo');
      // If there's an active group filter and the assignment moved the eval out, re-fetch
      if (groupFilter !== 'all') {
        loadAll();
      }
    } catch (e) {
      toast.error(e?.message || 'No se pudo cambiar el grupo');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.deleteEvaluation(deleteId);
      setEvaluations(evaluations.filter(e => e.id !== deleteId));
      toast.success('Entrevista eliminada');
    } catch (error) {
      toast.error('Error al eliminar entrevista');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    const matchesSearch = evaluation.student_name.toLowerCase().includes(searchTerm.toLowerCase());
    const estado = getEstado(evaluation);
    // Compat con valores legacy del filtro (bajo/moderado/alto) y nombres viejos de estado.
    const FILTER_TO_ESTADO = {
      bajo: ['PUEDE_INICIAR', 'APTO_PARA_EJERCICIO_MODERADO'],
      moderado: ['APTO_PARA_PROGRESAR_CON_CONTROL'],
      alto: ['APTO_REQUERIDO'],
    };
    const matchesRisk =
      filterRisk === 'all' ||
      (FILTER_TO_ESTADO[filterRisk]
        ? FILTER_TO_ESTADO[filterRisk].includes(estado)
        : estado === filterRisk);
    const showAlerts = searchParams.get('filter') === 'alerts';

    // Filtro "Pendientes de apto médico": estados que requieren apto + aún no entregado.
    const APTO_REQUIRED_ESTADOS = ['DERIVACION_INMEDIATA', 'APTO_REQUERIDO', 'APTO_PARA_PROGRESAR_CON_CONTROL'];
    const matchesPendingApto = !filterPendingApto || (
      APTO_REQUIRED_ESTADOS.includes(estado) && !evaluation.apto_medico_entregado
    );

    if (showAlerts) {
      return matchesSearch && estado === 'DERIVACION_INMEDIATA' && matchesPendingApto;
    }

    return matchesSearch && matchesRisk && matchesPendingApto;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1F3A5F]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="evaluations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-['Inter']">Entrevistas</h1>
            <p className="text-gray-500 text-sm mt-1 font-['Inter']">
              {evaluations.length} {evaluations.length === 1 ? 'entrevista' : 'entrevistas'} registradas
            </p>
          </div>
          <Button 
            onClick={() => navigate('/new-evaluation')} 
            className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
            data-testid="new-evaluation-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Entrevista
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowIntakeModal(true)}
            className="border-[#1F3A5F]/30 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 font-['Inter']"
            data-testid="generate-intake-link-btn"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            Generar link de entrevista
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-gray-200 bg-white">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-200 font-['Inter']"
                  data-testid="search-input"
                />
              </div>
              <Select value={filterRisk} onValueChange={setFilterRisk}>
                <SelectTrigger className="w-full sm:w-48 border-gray-200 font-['Inter']" data-testid="filter-risk">
                  <Filter className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Filtrar por riesgo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="bajo">Riesgo Bajo</SelectItem>
                  <SelectItem value="moderado">Riesgo Moderado</SelectItem>
                  <SelectItem value="alto">Riesgo Alto</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupFilter} onValueChange={onChangeGroupFilter}>
                <SelectTrigger className="w-full sm:w-52 border-gray-200 font-['Inter']" data-testid="filter-group">
                  <Users className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Filtrar por grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los grupos</SelectItem>
                  <SelectItem value="none">Sin grupo</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id} data-testid={`filter-group-option-${g.id}`}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggle: pendientes de apto médico */}
            <label
              className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none text-sm transition-colors ${
                filterPendingApto
                  ? 'bg-amber-50 border-amber-300 text-amber-900'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="filter-pending-apto-wrapper"
            >
              <input
                type="checkbox"
                checked={filterPendingApto}
                onChange={(e) => setFilterPendingApto(e.target.checked)}
                data-testid="filter-pending-apto"
                className="h-4 w-4 accent-amber-600 cursor-pointer"
              />
              <span className="font-medium">Mostrar solo pendientes de apto médico</span>
            </label>
          </CardContent>
        </Card>

        {/* Alerts Filter Active */}
        {searchParams.get('filter') === 'alerts' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-red-700 font-medium font-['Inter']">
                Mostrando solo personas con signos de alarma
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/evaluations')}
              className="text-red-600 hover:text-red-700 font-['Inter']"
            >
              Quitar filtro
            </Button>
          </div>
        )}

        {/* Group filter banner */}
        {groupFilter !== 'all' && (
          <div className="p-4 bg-[#1F3A5F]/5 border border-[#1F3A5F]/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#1F3A5F]" />
              <span className="text-[#1F3A5F] font-medium font-['Inter']">
                {groupFilter === 'none'
                  ? 'Mostrando solo entrevistas sin grupo'
                  : `Filtrando por grupo: ${groupNameById.get(groupFilter) || groupFilter}`}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChangeGroupFilter('all')}
              className="text-[#1F3A5F] hover:text-[#2a4a73] font-['Inter']"
              data-testid="clear-group-filter"
            >
              Quitar filtro
            </Button>
          </div>
        )}

        {/* Evaluations List */}
        {filteredEvaluations.length === 0 ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="h-7 w-7 text-gray-400" />
                </div>
                <h3 className="font-medium text-gray-700 mb-1 font-['Inter']">
                  {searchTerm || filterRisk !== 'all' || groupFilter !== 'all' ? 'Sin resultados' : 'Sin entrevistas'}
                </h3>
                <p className="text-sm text-gray-500 mb-4 font-['Inter']">
                  {searchTerm || filterRisk !== 'all' || groupFilter !== 'all'
                    ? 'No hay entrevistas que coincidan con los filtros aplicados'
                    : 'Creá la primera entrevista para comenzar'}
                </p>
                {!searchTerm && filterRisk === 'all' && groupFilter === 'all' && (
                  <Button onClick={() => navigate('/new-evaluation')} size="sm" className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']">
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Entrevista
                  </Button>
                )}
                {groupFilter !== 'all' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onChangeGroupFilter('all')}
                    className="border-gray-200 font-['Inter']"
                    data-testid="empty-clear-group-filter"
                  >
                    Ver todas las entrevistas
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredEvaluations.map((evaluation) => (
              <Card 
                key={evaluation.id} 
                className="border-gray-200 bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/evaluation/${evaluation.id}`)}
                data-testid={`evaluation-card-${evaluation.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-[#1F3A5F]/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-[#1F3A5F]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-800 font-['Inter']">{evaluation.student_name}</h3>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className={cn(
                                  "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border font-['Inter'] transition-colors",
                                  evaluation.group_id && groupNameById.get(evaluation.group_id)
                                    ? "border-[#1F3A5F]/20 bg-[#1F3A5F]/5 text-[#1F3A5F] hover:bg-[#1F3A5F]/10"
                                    : "border-dashed border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-700"
                                )}
                                data-testid={`group-badge-${evaluation.id}`}
                                aria-label="Asignar grupo"
                              >
                                <Users className="h-3 w-3" />
                                <span className="max-w-[120px] truncate">
                                  {evaluation.group_id && groupNameById.get(evaluation.group_id)
                                    ? groupNameById.get(evaluation.group_id)
                                    : 'Sin grupo'}
                                </span>
                                <ChevronDown className="h-3 w-3 opacity-60" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                              className="w-56"
                            >
                              <DropdownMenuLabel className="font-['Inter']">Asignar a grupo</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={(e) => {
                                  e.preventDefault();
                                  if (evaluation.group_id) handleAssignGroup(evaluation.id, null);
                                }}
                                className="font-['Inter']"
                                data-testid={`group-option-none-${evaluation.id}`}
                              >
                                <span className="flex-1">Sin grupo</span>
                                {!evaluation.group_id && <Check className="h-4 w-4 text-[#1F3A5F]" />}
                              </DropdownMenuItem>
                              {groups.length > 0 && <DropdownMenuSeparator />}
                              {groups.map((g) => (
                                <DropdownMenuItem
                                  key={g.id}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    if (evaluation.group_id !== g.id) handleAssignGroup(evaluation.id, g.id);
                                  }}
                                  className="font-['Inter']"
                                  data-testid={`group-option-${g.id}-${evaluation.id}`}
                                >
                                  <span className="flex-1 truncate">{g.name}</span>
                                  {evaluation.group_id === g.id && <Check className="h-4 w-4 text-[#1F3A5F]" />}
                                </DropdownMenuItem>
                              ))}
                              {groups.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-gray-500 italic font-['Inter']">
                                  No hay grupos. Creá uno desde el menú "Grupos".
                                </div>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {getEstado(evaluation) === 'DERIVACION_INMEDIATA' && (
                            <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 text-xs font-['Inter']">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Alerta
                            </Badge>
                          )}
                          {isPendienteRevision(evaluation) && (
                            <Badge
                              variant="outline"
                              className="border-blue-200 bg-blue-50 text-blue-700 text-xs font-['Inter']"
                              data-testid={`intake-pending-badge-${evaluation.id}`}
                            >
                              <ClipboardList className="h-3 w-3 mr-1" />
                              Completado por alumno — pendiente de revisión
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 font-['Inter']">
                          <span>{evaluation.age} años</span>
                          <span>·</span>
                          <span>{evaluation.sex}</span>
                          <span>·</span>
                          <span>IMC: {evaluation.imc}</span>
                          <span>·</span>
                          <span>{getActivityLabel(evaluation.activity_level)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 ml-15 md:ml-0">
                      <div className="hidden sm:flex gap-6 text-right">
                        <div>
                          <p className="text-xs text-gray-400 font-['Inter']">FCM</p>
                          <p className="font-mono font-semibold text-gray-700">{evaluation.max_heart_rate ? Math.round(evaluation.max_heart_rate) : '-'}</p>
                        </div>
                      </div>
                      <Badge className={cn("font-semibold text-xs px-3 py-1 border font-['Inter']", getDecisionBadgeClass(getEstado(evaluation)))}>
                        {getDecisionLabel(getEstado(evaluation))}
                      </Badge>
                      <AptoMedicoChip evaluation={evaluation} />
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-gray-400 hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/evaluation/${evaluation.id}`);
                          }}
                          data-testid={`view-${evaluation.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(evaluation.id);
                          }}
                          className="text-gray-400 hover:text-red-600"
                          data-testid={`delete-${evaluation.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cardiorespiratory Metrics */}
                  <CardioMetrics evaluation={evaluation} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Student intake link modal (Fase B) */}
        <StudentIntakeLinkModal open={showIntakeModal} onOpenChange={setShowIntakeModal} />

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-['Inter']">¿Eliminar entrevista?</AlertDialogTitle>
              <AlertDialogDescription className="font-['Inter']">
                Esta acción no se puede deshacer. La entrevista será eliminada permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-gray-200 font-['Inter']" data-testid="cancel-delete">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-['Inter']"
                data-testid="confirm-delete"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
